/**
 * Breaking-news reader (F6 reading redesign, Stage 2).
 *
 * Content-forward read in the dark shell, matching the article exemplar
 * (ArticlePageContent). Article column (~720px readable measure) + a
 * right-hand CONTEXT COLUMN (entities in this piece + more on these). The
 * piece IS the conclusion — no synthetic assessment header, no claps, no
 * reactions, no author link, no prev/next nav, no market-watch sidebar.
 *
 * Breaking news carries a REAL source of record (the source tweet), so the
 * shared PromotedSource block lights up directly under the body. Provenance
 * is a feature, not a footnote.
 *
 * Preserved verbatim: the markdown/embed pipeline (ReactMarkdown + remarkGfm
 * + rehypeRaw + image-sizing + TwitterEmbed routing + TagMention parsing +
 * ErrorBoundary), the coverage-lag note, SocialShare, and the props signature.
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { BreakingNews } from '../../../shared/types/breaking-news';
import { ArrowLeft } from 'lucide-react';
import { tagService } from '@/lib/tag-service';
import { Tag } from '@/types/tags';
import { Article } from '@/types/content';
import DeskByline from '@/components/ui/DeskByline';
import { type Provenance } from '@/lib/provenance';
import { isReceiptEligible, receiptPagePath, type RawDoc } from '@/lib/receipts';
import SocialShare from '@/components/SocialShare';
import { formatRelativeDate, formatRelativeTime } from '@/utils/date-formatting';
import { coverageLagForDoc } from '@/lib/coverage-lag';
import { processMarkdownForSizing, extractImageWidth } from '@/utils/markdown-utils';
import { getArticlePath } from '@/lib/article-url-utils';
import { urlLabel } from '@/lib/url-label';
import { TagMention } from '@/components/ui/TagMention';
import { MentionDedupeProvider } from '@/components/ui/MentionDedupe';
import { parseTextWithMentions, extractTwitterHandleFromHref } from '@/utils/markdown-utils';
import PromotedSource from '@/components/pages/reading/PromotedSource';
import SourceReceipts from '@/components/pages/reading/SourceReceipts';
import StoryArcRail from '@/components/pages/reading/StoryArcRail';
// Type-only: erased at compile time, so the server resolver never enters this client bundle.
import type { StoryArc } from '@/lib/server/story-arc';
import CorrectionNotice from '@/components/content/CorrectionNotice';
import type { SupersededLink } from '@/lib/corrections';
import PieceContext, {
  PieceContextEntity,
  PieceContextMore,
} from '@/components/pages/reading/PieceContext';

// Import TwitterEmbed directly (no dynamic import to avoid hydration issues)
import { TwitterEmbed } from '@/components/ui/SocialEmbed';
import ArticleAdSpot from '@/components/ui/ArticleAdSpot';
import { ErrorBoundary } from '@/components/utils/ErrorBoundary';
import EntityTrajectoryStub, {
  type EntityTrajectoryStubData,
} from '@/components/trajectory/EntityTrajectoryStub';

// Inline fallback for the markdown body — keeps the rest of the page
// (title, source, context) usable when the body itself crashes.
function MarkdownErrorFallback({ resetError }: { error?: Error; resetError: () => void }) {
  return (
    <div className="my-4 rounded border-[0.5px] border-divider bg-surface p-4 text-sm text-parchment-dim">
      <p className="mb-1 font-medium text-parchment">Story content couldn't render right now.</p>
      <p className="text-xs text-parchment-dim">
        Refresh the page or{' '}
        <button onClick={resetError} className="underline transition-colors hover:text-scout-gold">try again</button>.
      </p>
    </div>
  );
}

interface BreakingNewsPageContentProps {
  breakingNews: BreakingNews;
  /** Programmatic subject-entity trajectory. null/absent = render nothing (no subject / untracked). */
  trajectory?: EntityTrajectoryStubData | null;
  /** R3 corrections desk: server-resolved forward pointer (beats are corrected-in-place, so
   * usually null — supersede is scoped to articles — but the notice still renders for corrections). */
  supersededLink?: SupersededLink | null;
  /**
   * CS1b — server-resolved honest provenance WITH the gold verdict AND the masthead identity map
   * wired in (resolveContentProvenance). REQUIRED, deliberately: this used to be optional with a
   * `?? deriveProvenance(doc, {kind})` fallback, which looked harmless but re-derived with neither
   * the gold verdict nor any identity — a detail route that forgot the prop would have silently
   * under-claimed to grammar B on a piece with a provable human reviewer. Making it required moves
   * that from "silent forever" to "will not compile".
   */
  provenance: Provenance;
  /**
   * Leg 6 C4 — "this story so far": the current beat's siblings in its story arc, already
   * resolved AND GATED server-side (lib/server/story-arc.ts). null = render no rail, which
   * covers an ungrouped beat, a missing story, and — importantly — an arc detectMultiEvent
   * flagged as mixing two events. The gate lives at the resolver, never here.
   */
  storyArc?: StoryArc | null;
  /**
   * Leg 6 C4 — source receipts: the tweets in this beat's merged event that the body does
   * not already embed (lib/beat-receipts.ts). Empty on single-tweet beats.
   */
  receiptTweetIds?: string[];
}

/** Pick the best thumbnail for an entity card (primary image, else first). */
function entityImage(tag: Tag): string | undefined {
  if (!tag.images || tag.images.length === 0) return undefined;
  const primary = tag.images.find((img) => img.isPrimary);
  return (primary || tag.images[0])?.url;
}

export default function BreakingNewsPageContent({ breakingNews, trajectory = null, supersededLink = null, provenance, storyArc = null, receiptTweetIds = [] }: BreakingNewsPageContentProps) {
  const [contextEntities, setContextEntities] = useState<PieceContextEntity[]>([]);
  const [relatedNews, setRelatedNews] = useState<BreakingNews[]>([]);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  // CS1 — honest byline provenance arrives fully resolved from the server page (lane +
  // human-verdict transitions + gold verdict + masthead identity), NEVER derived from
  // breakingNews.author (the owner uid the auto-publisher stamps). No local fallback: a
  // client-side re-derivation could not see the gold record or the identity map anyway, so it
  // could only ever under-claim.

  // Process markdown content for image sizing (memoized to prevent re-processing)
  const processedContent = React.useMemo(
    () => processMarkdownForSizing(breakingNews.content),
    [breakingNews.content]
  );

  // Extract width from first image if it's the featured image (memoized)
  const featuredImageWidth = React.useMemo(
    () => extractImageWidth(breakingNews.content, breakingNews.featuredImage),
    [breakingNews.content, breakingNews.featuredImage]
  );

  // A9a — when we covered a source tweet > 24h after it posted (publishedAt is
  // backdated to the tweet time by design), say so honestly. null on the fresh
  // path / human posts → renders nothing.
  const coverageLag = React.useMemo(
    () => coverageLagForDoc(breakingNews),
    [breakingNews]
  );

  // CONTEXT COLUMN: resolve breakingNews.tags[] -> entity dossier cards.
  // Entity-only (must have entityType); preserve tag order; cap at 6.
  const loadEntities = useCallback(async () => {
    const tagIds = (breakingNews.tags ?? []).slice(0, 12);
    if (tagIds.length === 0) {
      setContextEntities([]);
      return;
    }

    try {
      const tagMap = await tagService.getTagsBatch(tagIds);
      const resolved: PieceContextEntity[] = [];
      for (const id of tagIds) {
        const tag = tagMap.get(id);
        if (!tag || tag.isActive === false) continue;
        if (!tag.entityType) continue; // descriptive tags are not dossiers
        resolved.push({
          slug: tag.slug,
          name: tag.name,
          image: entityImage(tag),
          type: tag.entityType || undefined,
        });
        if (resolved.length >= 6) break;
      }
      setContextEntities(resolved);
    } catch (err) {
      console.error('Failed to resolve entities for context column:', err);
      setContextEntities([]);
    }
  }, [breakingNews.tags]);

  const loadRelatedNews = useCallback(async () => {
    const tagIds = breakingNews.tags ?? [];
    if (tagIds.length === 0) return;

    try {
      // Fetch news with same tag, recency-sorted
      const response = await fetch(
        `/api/breaking-news?tagId=${tagIds[0]}&limit=6&status=published&sortBy=publishedAt`
      );

      if (!response.ok) return;

      const data = await response.json();
      const allNews = data.data.breakingNews || [];

      // Filter out current article
      const related = allNews.filter((item: BreakingNews) => item.id !== breakingNews.id);
      setRelatedNews(related.slice(0, 6));
    } catch (err) {
      console.error('Failed to load related news:', err);
    }
  }, [breakingNews.id, breakingNews.tags]);

  const loadRelatedArticles = useCallback(async () => {
    const tagIds = breakingNews.tags ?? [];
    if (tagIds.length === 0) return;

    try {
      // Fetch articles with overlapping tags
      const response = await fetch(
        `/api/articles?tags=${tagIds.join(',')}&limit=6&status=published`
      );

      if (!response.ok) return;

      const data = await response.json();
      const articles = data.data.articles || [];
      setRelatedArticles(articles.slice(0, 6));
    } catch (err) {
      console.error('Failed to load related articles:', err);
    }
  }, [breakingNews.tags]);

  useEffect(() => {
    // Load all data in parallel for faster page render
    Promise.all([
      loadEntities(),
      loadRelatedNews(),
      loadRelatedArticles(),
    ]).catch((error) => {
      console.error('Error loading breaking news data:', error);
    });
  }, [breakingNews.id, loadEntities, loadRelatedNews, loadRelatedArticles]);

  // Track view event on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag && breakingNews.id) {
      (window as any).gtag('event', 'breaking_news_view', {
        breaking_news_id: breakingNews.id,
        breaking_news_slug: breakingNews.slug,
        has_title: !!breakingNews.title,
        tag_count: breakingNews.tags?.length ?? 0,
      });
    }
  }, [breakingNews.id, breakingNews.slug, breakingNews.title, breakingNews.tags?.length]);

  // Beats already shown by the arc rail. "More on these" recalls by ENTITY and the rail by
  // STORY, so a sibling in this beat's arc lands in both — the same link twice in one
  // column. The rail wins: it is the more specific placement and it carries the ordering.
  const arcHrefs = React.useMemo(
    () => new Set([...(storyArc?.earlier ?? []), ...(storyArc?.later ?? [])].map((i) => i.href)),
    [storyArc],
  );

  // CONTEXT COLUMN: "MORE ON THESE" — merge related breaking + related
  // articles, rank by RECENCY (publishedAt desc, NOT claps), exclude the
  // current item and anything the arc rail already shows, cap at 5.
  const moreOnThese: PieceContextMore[] = React.useMemo(() => {
    type Ranked = { href: string; title: string; eyebrow?: string; ts: number };

    const fromNews: Ranked[] = (relatedNews || [])
      .filter((n) => n.id !== breakingNews.id && n.slug !== breakingNews.slug)
      .map((n) => {
        const when = n.publishedAt || n.createdAt;
        const ts = when ? new Date(when).getTime() : 0;
        const rel = when ? formatRelativeTime(when) : '';
        return {
          href: `/breaking/${n.slug}`,
          title: n.title || n.excerpt || 'Breaking update',
          eyebrow: rel ? `Breaking · ${rel}` : 'Breaking',
          ts,
        };
      });

    const fromArticles: Ranked[] = (relatedArticles || []).map((a) => {
      const ts = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const typeLabel =
        a.contentType === 'guide'
          ? 'Guide'
          : a.contentType === 'analysis'
            ? 'Analysis'
            : 'News';
      const rel = a.publishedAt ? formatRelativeTime(a.publishedAt) : '';
      return {
        href: getArticlePath(a),
        title: a.title,
        eyebrow: rel ? `${typeLabel} · ${rel}` : typeLabel,
        ts,
      };
    });

    return [...fromNews, ...fromArticles]
      .filter((item) => !arcHrefs.has(item.href))
      .sort((a, b) => b.ts - a.ts)
      .slice(0, 5)
      .map(({ href, title, eyebrow }) => ({ href, title, eyebrow }));
  }, [relatedNews, relatedArticles, breakingNews.id, breakingNews.slug, arcHrefs]);

  // Memoize markdown components to prevent TwitterEmbed from remounting
  const markdownComponents = React.useMemo(() => ({
    // Override paragraph to prevent nesting issues - must check node type BEFORE rendering
    p: ({ children, node, ...props }: any) => {
      // Check if this paragraph contains only an image (which might be a TwitterEmbed)
      // If so, don't wrap it in a <p> tag at all
      if (node && node.children && node.children.length === 1 && node.children[0].tagName === 'img') {
        return <>{children}</>;
      }

      // Detect block-level elements that can't be nested in <p> tags
      // This prevents hydration errors with markdown content containing:
      // - Images, divs, blockquotes, Twitter embeds, etc.
      const childArray = React.Children.toArray(children);

      // Recursive function to check if any child (or nested child) is a block-level element
      const hasBlockLevelChild = (children: any): boolean => {
        const childArray = React.Children.toArray(children);

        return childArray.some((child) => {
          if (React.isValidElement(child)) {
            const childType = child.type;

            // String type names (HTML elements)
            if (typeof childType === 'string') {
              const blockElements = ['div', 'blockquote', 'figure', 'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'pre', 'hr', 'p'];
              if (blockElements.includes(childType)) {
                return true;
              }
            }

            // Check for Suspense boundaries (used by dynamic imports like TwitterEmbed)
            if (childType && (childType.toString().includes('Suspense') || childType.toString().includes('Loadable'))) {
              return true;
            }

            // Component types (like TwitterEmbed)
            // Check by displayName or tweetUrl prop (TwitterEmbed has tweetUrl prop)
            if (childType === TwitterEmbed ||
              (childType && typeof childType === 'function' && childType.name === 'TwitterEmbed') ||
              (child.props && child.props.tweetUrl)) {
              return true;
            }

            // Recursively check children
            if (child.props && child.props.children) {
              return hasBlockLevelChild(child.props.children);
            }
          }
          return false;
        });
      };

      // If contains block-level elements, use div wrapper
      if (hasBlockLevelChild(childArray)) {
        return <div className="my-4">{children}</div>;
      }

      // Otherwise, render as normal paragraph
      const childrenWithMentions = React.Children.map(children, child => {
        if (typeof child === 'string') {
          const segments = parseTextWithMentions(child);
          return segments.map((segment, index) => {
            if (segment.type === 'mention') {
              return <TagMention key={`mention-${index}`} slug={segment.value} />;
            }
            return segment.value;
          });
        }
        return child;
      });

      return <p className="mb-4 break-words font-body text-lg leading-relaxed text-parchment/90" {...props}>{childrenWithMentions}</p>;
    },
    a: ({ href, children }: any) => {
      // Inline twitter/x.com profile links bypass the @mention parser; route
      // them back through the entity chip (rich when a tag exists + de-duped to
      // first mention, plain external link when no tag). Only when the link text
      // is a bare @handle — don't swallow real sentence copy linked to a profile.
      const twitterHandle = extractTwitterHandleFromHref(href);
      const linkText =
        typeof children === 'string'
          ? children
          : Array.isArray(children) && children.length === 1 && typeof children[0] === 'string'
            ? children[0]
            : null;
      if (twitterHandle && linkText && /^@?[A-Za-z0-9_]{1,15}$/.test(linkText.trim())) {
        return <TagMention slug={twitterHandle} />;
      }
      return (
        <a
          href={href}
          className="break-words text-scout-gold underline transition-colors hover:text-scout-gold/80"
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      );
    },
    blockquote: ({ children, ...props }: any) => {
      const childrenWithMentions = React.Children.map(children, child => {
        if (typeof child === 'string') {
          const segments = parseTextWithMentions(child);
          return segments.map((segment, index) => {
            if (segment.type === 'mention') {
              return <TagMention key={`mention-${index}`} slug={segment.value} />;
            }
            return segment.value;
          });
        }
        return child;
      });

      // Ensure blockquotes are never wrapped in <p> tags
      return (
        <blockquote className="my-6 rounded-r-lg border-l-4 border-scout-gold bg-surface py-4 pl-6 italic text-parchment-dim" {...props}>
          {childrenWithMentions}
        </blockquote>
      );
    },
    ul: ({ children }: any) => (
      <ul className="my-4 ml-6 list-outside list-disc space-y-2 pl-2 text-parchment/90">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="my-4 ml-6 list-outside list-decimal space-y-2 pl-2 text-parchment/90">
        {children}
      </ol>
    ),
    li: ({ children }: any) => {
      const childrenWithMentions = React.Children.map(children, child => {
        if (typeof child === 'string') {
          const segments = parseTextWithMentions(child);
          return segments.map((segment, index) => {
            if (segment.type === 'mention') {
              return <TagMention key={`mention-${index}`} slug={segment.value} />;
            }
            return segment.value;
          });
        }
        return child;
      });
      return <li className="break-words leading-relaxed text-parchment/90">{childrenWithMentions}</li>;
    },
    img: ({ src, alt }: { src?: string; alt?: string }) => {
      // Extract width from src if present
      let width: string | undefined = undefined;
      let cleanSrc = src || '';

      // Check for {width=X} syntax (URL-encoded or plain)
      const encodedMatch = cleanSrc.match(/%7Bwidth=(\d+(?:%|px)?)%7D$/i);
      if (encodedMatch) {
        width = encodedMatch[1];
        cleanSrc = cleanSrc.replace(/%7Bwidth=\d+(?:%|px)?%7D$/i, '');
      } else {
        const plainMatch = cleanSrc.match(/\{width=(\d+(?:%|px)?)\}$/);
        if (plainMatch) {
          width = plainMatch[1];
          cleanSrc = cleanSrc.replace(/\{width=\d+(?:%|px)?\}$/, '');
        }
      }

      // Check for Twitter URLs - return TwitterEmbed directly (wrapped in fragment to mark as special)
      const twitterMatch = cleanSrc.match(/(?:twitter\.com|x\.com)\/[\w]+\/status\/(\d+)/);
      if (twitterMatch) {
        const normalizedUrl = cleanSrc.replace(/x\.com/, 'twitter.com');
        // Return TwitterEmbed with a special data attribute so p component can detect it
        return <TwitterEmbed key={normalizedUrl} tweetUrl={normalizedUrl} caption={alt} />;
      }

      // Hide first image if it's the same as featured image (to avoid duplication)
      if (breakingNews.featuredImage && cleanSrc === breakingNews.featuredImage) {
        return null;
      }

      // Regular image - no wrapper, let p component handle it
      return (
        <img
          src={cleanSrc}
          alt={alt || ''}
          className="my-4 mx-auto block h-auto rounded-lg"
          style={width ? { maxWidth: width.endsWith('%') || width.endsWith('px') ? width : `${width}px` } : { maxWidth: '100%' }}
        />
      );
    },
  }), [breakingNews.featuredImage]); // Only recreate if featured image changes

  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://descout.xyz'}/breaking/${breakingNews.slug}`;
  const publishedAt = breakingNews.publishedAt || breakingNews.createdAt;

  return (
    <div className="py-8 lg:py-10">
      {/* Capped + centered two-column read: article keeps a comfortable measure,
          context is a true sidebar (not a column that balloons to fill the
          remaining desktop width). Mirrors ArticlePageContent. Cap math:
          ~720 article + 320 sidebar + 56px (gap-14). Single column on mobile. */}
      <div className="mx-auto grid max-w-[1120px] grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-14">
        {/* ARTICLE COLUMN */}
        <div className="min-w-0">
          <article>
            {/* Back link */}
            <div className="mb-6">
              <Link
                href="/breaking"
                className="inline-flex items-center font-mono text-[11px] uppercase tracking-[0.14em] text-parchment-dim transition-colors hover:text-scout-gold"
              >
                <ArrowLeft className="mr-2 h-3.5 w-3.5" />
                Back to Breaking News
              </Link>
            </div>

            {/* Eyebrow: BREAKING · time (the header block is gone; the title
                lives in the body) */}
            <div className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] uppercase tracking-[0.14em] text-parchment-dim">
              <span className="text-scout-gold">Breaking</span>
              <span>·</span>
              <time>{formatRelativeDate(publishedAt)}</time>
              {coverageLag && (
                <>
                  <span>·</span>
                  <span className="normal-case italic tracking-normal">
                    Posted {coverageLag.postedLabel} — covered {coverageLag.coveredLabel}
                  </span>
                </>
              )}
            </div>

            {/* Title — in the body, content-forward */}
            {breakingNews.title && (
              <h1 className="mb-4 font-display text-3xl font-bold leading-[1.15] tracking-[-0.02em] text-parchment md:text-4xl lg:text-[2.75rem]">
                {breakingNews.title}
              </h1>
            )}

            {/* CS1 — honest byline: producer lane + (only when a real human verdict record
                backs it) the reviewer's masthead identity; else the auto-published grammar. */}
            <DeskByline
              provenance={provenance}
              receiptHref={
                breakingNews.id && isReceiptEligible(breakingNews as unknown as RawDoc, 'breaking')
                  ? receiptPagePath('breaking', breakingNews.id)
                  : undefined
              }
            />

            {/* Corrections desk (R3): dated notice when this beat was corrected.
                Renders NOTHING when uncorrected — byte-identical to before. */}
            <CorrectionNotice
              correction={breakingNews.correction}
              transitionLog={breakingNews.transitionLog}
              supersededLink={supersededLink}
            />

            {/* Featured Image - skip if it's a Twitter URL */}
            {breakingNews.featuredImage && !breakingNews.featuredImage.match(/(?:twitter\.com|x\.com)\/[\w]+\/status\/\d+/) && (
              <div className="mb-8 flex justify-center">
                <img
                  src={breakingNews.featuredImage}
                  alt={`${breakingNews.title || 'Breaking news'} - Abstract blockchain breaking news`}
                  className="rounded-lg border-[0.5px] border-divider"
                  style={featuredImageWidth ? {
                    maxWidth: featuredImageWidth.endsWith('%') || featuredImageWidth.endsWith('px')
                      ? featuredImageWidth
                      : `${featuredImageWidth}px`,
                    width: '100%',
                    height: 'auto'
                  } : {
                    maxWidth: '600px',
                    width: '100%',
                    height: 'auto',
                    maxHeight: '500px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}

            {/* Content — wrapped so a markdown / TwitterEmbed crash doesn't take
                down the whole page. */}
            <div className="prose prose-invert prose-lg max-w-none break-words">
              <ErrorBoundary fallback={MarkdownErrorFallback}>
                <MentionDedupeProvider>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    skipHtml={false}
                    components={markdownComponents}
                  >
                    {processedContent}
                  </ReactMarkdown>
                </MentionDedupeProvider>
              </ErrorBoundary>
            </div>

            {/* PROMOTED SOURCE — breaking news carries a real source of record
                (the source tweet). Provenance is a feature, not a footnote.
                Renders nothing if there is no handle/url. */}
            <PromotedSource
              handle={breakingNews.sourceUsername}
              time={breakingNews.sourceTweetDate ?? breakingNews.publishedAt}
              url={breakingNews.tweetUrl}
            />

            {/* SOURCE RECEIPTS — a beat now covers an EVENT, not a tweet, so it can stand on
                several source tweets. The body embeds the canonical one; these are the rest.
                One beat, N receipts. Renders nothing when the beat didn't merge. */}
            <SourceReceipts tweetIds={receiptTweetIds} />

            {/* REFERENCED — the outbound links the source tweet shared (external-coverage
                v2). Small + unobtrusive; renders nothing when the beat carried no links. */}
            {Array.isArray(breakingNews.sourceLinks) && breakingNews.sourceLinks.length > 0 && (
              <div className="mt-6 border-t-[0.5px] border-divider pt-4">
                <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.14em] text-parchment-dim">
                  Referenced
                </div>
                <ul className="space-y-1">
                  {breakingNews.sourceLinks.map((href) => (
                    <li key={href}>
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="break-all text-sm text-scout-gold transition-colors hover:text-scout-gold/80"
                      >
                        {urlLabel(href)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Social Share */}
            <div className="mt-8 border-t-[0.5px] border-divider pt-6">
              <SocialShare
                url={shareUrl}
                title={breakingNews.title || breakingNews.excerpt || 'Breaking News'}
                contentType="article"
                variant="inline"
              />
            </div>

            {/* Programmatic trajectory stub — the data-driven replacement for
                the banned "Descout tracks X as Building" writer prose. Renders
                nothing when there's no subject entity or no trajectory doc. */}
            <EntityTrajectoryStub data={trajectory ?? null} />

            {/* Footer Ad Spot */}
            <ArticleAdSpot location="breaking-news-footer" />
          </article>
        </div>

        {/* CONTEXT COLUMN — this story so far + entities in this piece + more on these.
            On mobile this stacks below the article (single column grid). */}
        <aside className="min-w-0">
          <div className="space-y-10 lg:sticky lg:top-8">
            {/* THIS STORY SO FAR — the arc from this beat's vantage. First in the column:
                it is the only block scoped to THIS story rather than to its entities.
                Renders nothing when there's no arc or the arc failed the multi-event gate. */}
            <StoryArcRail arc={storyArc} />

            <PieceContext entities={contextEntities} more={moreOnThese} />

            {/* Context-rail Ad Spot */}
            <div className="hidden lg:block">
              <ArticleAdSpot location="article-sidebar" />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
